<style scoped>
.playlist-container {
  margin-left: 30px;
  padding: 4px;
}
.drag-area {
  border-style: dashed;
  border-width: 1px;
  min-height: 50px;
}
.empty-wrapper {
  margin-top: 100px;
}
.channel-title {
  font-size: 24px;
}
.action-button {
  margin-right: 4px;
}
</style>

<template>
  <div v-loading="loading">
  <transition name="fade">
    <div v-if="flagFetchedChannel">
      <div v-if="channel.children.length > 0 || !isPreview">
        <div>
          <mu-button 
            icon 
            @click="onClickBack"
            v-if="!panel"
            >
            <mu-icon value="keyboard_backspace" />
          </mu-button>
          <span class="item-title" :class="panel ? '' : 'channel-title'">
            <mu-icon class="inline-icon" :class="panel ? '' : 'channel-title'" value="subscriptions" /> {{channel.name}}
          </span>
          <div class="float-right">
            <BookmarkButton 
              :id="channel._id"
              type="channel"
              :object="channel"
              />
            <ButtonOrListGroup :simple="panel" icon="add" v-if="edit">
              <template slot="list">
                <ManagePlaylist 
                  :editing="addingPlaylist" 
                  :title="$t('button.add') + $t('space') + $t('terms.playlist')"
                  @cancel="onCancel"
                  @save="onAddPlaylist"
                  create
                  >
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="playlist_add" 
                    color="primary" 
                    @click="addingPlaylist = true" 
                    :text="$t('button.add') + $t('space') + $t('terms.playlist')"
                    />
                </ManagePlaylist>
                <ManageMedia
                  :editing="addingMedia" 
                  :title="$t('button.add') + $t('space') + $t('terms.media')"
                  @cancel="onCancel"
                  @save="onAddMedia"
                  create
                  >
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="library_add" 
                    color="primary" 
                    @click="addingMedia = true" 
                    :text="$t('button.add') + $t('space') + $t('terms.media')"
                    />
                </ManageMedia>
              </template>
            </ButtonOrListGroup>
            <ManageChannel
              v-model="channel"
              :editing="editing" 
              :thumbnail="thumbnail" 
              :title="$t('button.edit') + $t('space') + $t('terms.channel')"
              @cancel="onCancel"
              @save="onEdit"
              @thumbnail="onThumbnail"
              create
              >
              <ButtonOrListGroup :simple="panel">
                <template slot="list">
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="edit" 
                    color="primary" 
                    @click="editing = true" 
                    :text="$t('button.edit') + $t('space') + $t('terms.channel')"
                    v-if="edit"
                    />
                  <ButtonOrListItem 
                    :simple="panel" 
                    small 
                    icon="image" 
                    color="primary" 
                    @click="thumbnail = true" 
                    :text="$t('ChannelItem.thumbnail.display')"
                    v-if="edit"
                    />
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="people" 
                    color="success" 
                    @click="toRoute('Enrollment')" 
                    :text="$t('terms.enrollment')"
                    v-if="isCourseChannel && editable && !isPublicChannel"
                    />
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="record_voice_over" 
                    color="success" 
                    @click="toRoute('Manager')" 
                    :text="$t('terms.admin')"
                    v-if="isCourseChannel && editable"
                    />
                  <ButtonOrListItem 
                    :simple="panel" 
                    icon="data_usage" 
                    color="secondary" 
                    @click="toRoute('AnalyticsHome')" 
                    :text="$t('terms.analytics')"
                    v-if="isCourseChannel && editable"
                    />
                </template>
              </ButtonOrListGroup>
            </ManageChannel>
          </div>
        </div>
        <div v-if="!panel" class="float-right">
          <mu-button @click="onClickToggle(true)" small class="action-button" color="primary" :disabled="forceToggle">{{$t('ChannelItem.expandAll')}}</mu-button>
          <mu-button @click="onClickToggle(false)" small class="action-button" color="secondary" :disabled="!forceToggle">{{$t('ChannelItem.collapseAll')}}</mu-button>
        </div>
        <div class="clearfix" />
        <div :class="panel ? '' : 'playlist-container'" v-if="editableReady">
          <div v-if="channel.children.length === 0">
            <small>{{$t('ChannelItem.empty')}}</small>
          </div>
          <draggable :list="channel.children" :options="{ 'group': 'channel', 'disabled': isPreview, 'handle': '.drag-handle' }" @change="onSort" :class="edit && !panel ? 'drag-area' : ''">
            <component 
              :is="child.type === 'media' ? 'MediaItem' : 'PlaylistItem'" 
              :obj="child" 
              :editable="editable"
              :enrollments="enrollments"
              :simple="panel"
              :channelId="channelId"
              :mediaId="mediaId"
              :defaultToggle="recursiveSearch(child, mediaId)"
              :forceToggle="forceToggle"
              v-for="(child, index) in channel.children"
              :key="index"
              @deleted="onDeleted(index)"
              @updated="onUpdated"
              @update="onUpdate($event, index)"
              @sort="onSort"
              />
          </draggable>
        </div>
      </div>
      <div class="empty-wrapper">
        <Empty 
          :condition="isPreview && channel.children.length === 0" 
          message="Course content is not ready yet." />
      </div>
    </div>
  </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

import BookmarkButton from '@/components/Bookmark/Button'
import ManageChannel from '@/components/Channel/Manage/Main.vue'

import PlaylistItem from '@/components/Playlist/PlaylistItem.vue'
import ManagePlaylist from '@/components/Playlist/ManagePlaylist'

import MediaItem from '@/components/Media/MediaItem.vue'
import ManageMedia from '@/components/Media/ManageMedia'

import Empty from '@/components/Empty'

import ButtonOrListItem from '@/components/Common/ButtonOrListItem'
import ButtonOrListGroup from '@/components/Common/ButtonOrListGroup'

import theme from '@/mixins/theme.js'
import removeObj from '@/utils/removeObj.js'

import axios from "axios";

import langEn from './lang/en.json'
import langZh from './lang/zh.json'

export default {
  name: "V2ChannelItem",
  mixins: [theme],
  components: {
    draggable,
    MediaItem,
    PlaylistItem,
    ManageChannel,
    ManagePlaylist,
    ManageMedia,
    Empty,
    ButtonOrListItem,
    ButtonOrListGroup,
    BookmarkButton,
  },
  props: {
    channelId: {
      type: String,
      required: true
    },
    panel: {
      type: Boolean,
      default: false
    },
    mediaId: {
      type: String,
      default: ""
    },
  },
  data() {
    return {
      channel: {},
      enrollments: [],
      flagFetchedChannel: false,
      editable: false,
      loading: false,
      editableReady: false,
      
      editing: false,
      thumbnail: false,
      addingPlaylist: false,
      addingMedia: false,
      
      forceToggle: false,
    }
  },
  computed: {
    // From Store
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isPreview() {
      if(this.$store.getters.previewMode) {
        return true;
      } else {
        return false;
      }
    },
    isPublicChannel() {
      return this.channel.hasOwnProperty('public') && this.channel.public === true;
    },
    
    // Channel Related
    edit() {
      return !this.isPreview && this.editable;
    },
    isCourseChannel() {
      return this.channel.type === "course";
    },
  },
  watch: {
    channelId() {
      //this.checkPermit();
      this.fetchChannel();
    },
  },
  created() {
    //this.checkPermit();
    this.fetchChannel();
    this.$i18n.add('en', langEn); // Add English Translation
    this.$i18n.add('zh', langZh); // Add Traditional Chinese Translation
  },
  methods: {
    // Fetch
    checkPermit() {
      this.loading = true;
      axios.get(this.$store.getters.urlApi + "permit/channel/" + this.channelId, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(response => {
        var result = response.data.data.result;
        if(result) {
          this.fetchChannel();
        } else {
          this.$router.push({
              name: 'Home'
          });
        }
      });
    },
    fetchChannel() {
      axios.get(this.$store.getters.urlApi + "channel/" + this.channelId + '?all=1', {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(response => {
        this.$set(this.$data, 'channel', response.data.data.channel);
        this.$nextTick(() => {
          this.flagFetchedChannel = true;
          this.loading = false;
        });
        this.fetchAdmin();
      }).catch(err => {
        this.$router.push({
          name: "NotFound",
          params: {
            url: this.$route.path
          }
        });
      });
    },
    fetchAdmin() {
      axios.get(this.$store.getters.urlApi + "admin/channel/" + this.channelId, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(response => {
        this.editable = response.data.data.admin.user_ids.includes(this.$store.state.auth.user._id) || this.isAdmin;
        this.editableReady = true;
        if(this.editable) {
          this.fetchEnrollments();
        }
      });
    },
    fetchEnrollments() {
      axios.get(this.$store.getters.urlApi + "enrollment/channel/" + this.channelId, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(response => {
        this.enrollments = response.data.data.enrollments;
      });
    },
    
    // Helper
    toRoute(name) {
      this.$router.push({
        name: name,
        params: {
            channelId: this.channelId
        }
      });
    },
    onClickBack() {
      this.$router.push({
        name: 'Home'
      });
    },
    
    // Event in this layer
    onCancel() {
      this.addingPlaylist = false;
      this.addingMedia = false;
      this.editing = false;
      this.thumbnail = false;
    },
    onAddPlaylist(payload) {
      this.addingPlaylist = false;
      axios.post(this.$store.getters.urlApi + "playlist/channel/" + this.channelId, payload, {
        headers: {
          token: this.$store.state.auth.token,
          'content-type': 'application/json'
        },
      }).then(val => {
        this.$toast.success(this.$t('toast.create'));
        this.channel.children.push({
          id: val.data.data.playlist._id,
          type: 'playlist',
          children: [],
          obj: val.data.data.playlist
        });
        this.onSave(this.channel);
      });
    },
    onAddMedia(payload) {
      this.addingMedia = false;
      
      var formData = payload.file;
      var data = payload.data;
      var upload = payload.upload;
      axios.post(this.$store.getters.urlApi + "media/channel/" + this.channel._id, data, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(val => {
        if(upload) {
          formData.append('_method', "PUT");
          this.$store.commit('uploadQueueAppend', {
            uploadId: Math.random().toString(36).substring(7),
            formData: formData,
            url: "media/" + val.data.data.media._id,
            text: data['name']
          });
        }
        this.channel.children.push({
          id: val.data.data.media._id,
          type: 'media',
          obj: val.data.data.media
        });
        this.$toast.success(this.$t('toast.create'));
        this.onSave(this.channel);
      });
    },
    onEdit(payload) {
      this.editing = false;
      
      this.onSave(payload, true);
    },
    onSave(payload, toast = false) {
      axios.put(this.$store.getters.urlApi + "channel/" + this.channel._id, {
        name: this.channel.name,
        layout: this.channel.layout,
        alias: this.channel.alias,
        public: this.channel.public,
        children: removeObj(this.channel.children)
      }, {
        headers: {
          token: this.$store.state.auth.token,
          'content-type': 'application/json'
        },
      }).then(() => {
        if(toast) this.$toast.success(this.$t('toast.edit'));
        this.onUpdated();
      });
    },
    onThumbnail(payload) {
      this.thumbnail = false;
      axios.post(this.$store.getters.urlApi + "channel/" + this.channelId + "/thumbnail", payload, {
        headers: {
          token: this.$store.state.auth.token
        },
      }).then(() => {
        this.$toast.success(this.$t('toast.edit'));
        this.onUpdated();
      });
    },
    onClickToggle(newValue) {
      if(newValue) {
        axios.get(this.$store.getters.urlApi + "channel/" + this.channelId + '?all=1', {
          headers: {
            token: this.$store.state.auth.token
          }
        }).then(response => {
          this.$set(this.$data, 'channel', response.data.data.channel);
          this.forceToggle = true;
        });
      } else {
        this.forceToggle = false;
      }
    },
    
    // Event from children
    onUpdated() { // Edited children, need update
      this.fetchChannel();
    },
    onUpdate(payload, index) { // Added or removed children
      this.channel.children[index] = payload;
      this.onSave(this.channel);
    },
    onSort() { // Sorting
      this.onSave(this.channel, true);
    },
    onDeleted(index) { // Deleted children
      if(this.channel.children[index].hasOwnProperty('children') && Array.isArray(this.channel.children[index].children)) {
        var children = this.channel.children[index].children;
        children.forEach(child => {
          this.channel.children.push(child);
        });
      }
      this.channel.children.splice(index, 1);
      this.onSave(this.channel);
    },
    
    recursiveSearch(object, id) {
      if(object.id === id) {
        return true;
      } else {
        if(object.hasOwnProperty('children') && object.children.length > 0) {
          var result = false;
          object.children.forEach(child => {
            result = result || this.recursiveSearch(child, id);
          });
          return result;
        } else {
          return false;
        }
      }
      
    },
  },
};
</script>
